import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Music2, ArrowRight, Send } from 'lucide-react';
import { getArtists, getImageUrl } from '../utils/api';
import './ArtistsPage.css';

const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const data = await getArtists();
      setArtists(data || []);
    } catch (error) {
      console.error('Failed to load artists', error);
    } finally {
      setLoading(false);
    }
  };

  const getSocialLinks = (artist) => {
    return artist.social_links || artist.socialLinks || {};
  };

  if (loading) {
    return (
      <div className="artists-page">
        <section className="artists-hero">
          <div className="container">
            <h1>The Roster</h1>
            <p>Meet the artists shaping the future of electronic music</p>
          </div>
        </section>
        <section className="artists-content">
          <div className="container">
            <div className="loading-spinner">Loading artists...</div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="artists-page">
      {/* Hero Section */}
      <section className="artists-hero">
        <div className="artists-hero-overlay"></div>
        <div className="container artists-hero-content">
          <h1>The Roster</h1>
          <p>Meet the artists shaping the future of electronic music</p>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="artists-content">
        <div className="container">
          {artists.length === 0 ? (
            <div className="no-artists">
              <p>No artists yet. Check back soon!</p>
            </div>
          ) : (
            <div className="artists-grid">
              {artists.map((artist) => {
                const socialLinks = getSocialLinks(artist);
                
                return (
                  <div key={artist.id} className="artist-card-full" data-testid={`artist-card-${artist.id}`}>
                    <div className="artist-card-image">
                      <img 
                        src={getImageUrl(artist.image)} 
                        alt={artist.name}
                        loading="lazy"
                      />
                      <div className="artist-card-overlay">
                        <div className="artist-socials">
                          {socialLinks.instagram && (
                            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                              <Instagram size={22} />
                            </a>
                          )}
                          {socialLinks.spotify && (
                            <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" aria-label="Spotify">
                              <Music2 size={22} />
                            </a>
                          )}
                        </div>
                      </div>
                      {artist.featured && (
                        <span className="featured-badge">Featured</span>
                      )}
                    </div>
                    
                    <div className="artist-card-info">
                      <span className="artist-genre-tag">{artist.genre}</span>
                      <h2>{artist.name}</h2>
                      <p className="artist-bio-preview">{artist.bio}</p>
                      
                      <Link to={`/artists/${artist.slug}`} className="artist-profile-link" data-testid={`view-artist-${artist.slug}`}>
                        View Full Profile
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="artists-cta">
        <div className="container">
          <h2>Want to Join the Roster?</h2>
          <p>We're always looking for talented artists who share our passion for electronic music.</p>
          <div className="cta-buttons">
            <Link to="/submit-demo" className="btn btn-primary" data-testid="submit-demo-cta">
              <Send size={18} />
              Submit Your Demo
            </Link>
            <Link to="/benefits" className="btn btn-secondary">Learn About Benefits</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArtistsPage;
